// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import AsignarOPrestar, { plazoInicial } from "../components/taller/AsignarOPrestar";
import AvisoWhatsapp from "../components/taller/AvisoWhatsapp";
import DevolvioTodo, { UltimaDevolucion } from "../components/taller/DevolvioTodo";
import DibujoHerramienta from "../components/taller/DibujoHerramienta";
import SubirPapel from "../components/taller/SubirPapel";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import "../assets/css/herramientas.css";
import {
  contenidoDeCaja,
  cuando,
  delPanolOSinAsignar,
  DESTINATARIOS,
  destinatarioDe,
  devolver,
  diaAr,
  ELEMENTOS_COMUNES,
  entregar,
  entregasDe,
  ESTADOS_PAPEL,
  estadoPapel,
  hayLibres,
  itemsDeLoQueTiene,
  loQueTiene as traerLoQueTiene,
  MOTIVOS_CIERRE,
  nombreDe,
  notaDeEntrega,
  nombrePanol,
  ordenLoQueTiene,
  PANOL_POR_DEFECTO,
  PANOLES,
  papelDeLoQueTiene,
  papelesRepetidos,
  pelar,
  personas as traerPersonas,
  planQuitarCaja,
  plazoDePrestamo,
  plazoElegido,
  puedeVerEntregas,
  quitarCaja,
  quitarPapel,
  renglonTexto,
  sueltosDe,
  sumarRenglon,
  cambiarCantidad,
  cambiarNota,
  devolucionesDe,
  tablero as traerTablero,
} from "../services/herramientas";

/* La caja de una persona: lo que el pañol le entregó, sea mecánico o chofer.
 *
 * Es el tablero, pero sólo con lo que esa persona tiene ASIGNADO (Nico, 02/10:
 * «cuando se aprieta en la caja de herramientas se abre el tablero con las
 * herramientas que tiene asignadas»). No hay plazo ni demora: lo asignado es
 * de él. Si además tiene algo prestado del pañol, va abajo y aparte, que eso
 * sí vuelve.
 *
 * ── HERRAMIENTAS Y ELEMENTOS, LA MISMA CAJA (P-311) ─────────────────────────
 *
 * Nico, 05/10: *«La sección herramienta y entrega de elementos deberían ser
 * la misma y verse como lo hace herramientas. Al fin y al cabo salen tmb del
 * pañol pero el destinatario es un chofer y no un mecánico (única
 * diferencia)»*. Así que un chofer tiene caja como un mecánico, y lo que
 * cambia es el rótulo (elementos en vez de herramientas) y el texto del papel.
 * El tipo sale del padrón (`personas.puesto`).
 *
 * ── ENTREGAR ARMA EL PAPEL SOLO ─────────────────────────────────────────────
 *
 * *«Estaría bueno que al asignar nuevas herramientas ya haga el papel solo, se
 * imprima, se firma y se almacena»*. «Entregar» junta todo lo que se lleva
 * —del catálogo del pañol elegido, o escrito a mano si todavía no está en el
 * catálogo— y en una sola transacción anota la entrega (el papel) y saca las
 * herramientas del stock (`entregar_del_panol`). Después abre el papel listo
 * para imprimir; firmado, se le saca una foto y se sube acá, en «Papeles».
 *
 * Sacar cierra UNA entrega del catálogo (la más nueva). Es lo que pasa cuando
 * una herramienta se rompe, se pierde o vuelve al pañol; el historial no se
 * borra, y el papel que se firmó tampoco.
 */

const LS_PANOL = "hrr-panol-entrega";

function panolGuardado() {
  try {
    const v = window.localStorage.getItem(LS_PANOL);
    return PANOLES.some((p) => p.id === v) ? v : PANOL_POR_DEFECTO;
  } catch {
    return PANOL_POR_DEFECTO;
  }
}

function NuevaEntrega({ persona, destinatario, catalogo, onHecho, onCerrar }) {
  const [panol, setPanol] = useState(panolGuardado);
  const [busca, setBusca] = useState("");
  const [renglones, setRenglones] = useState([]);
  const [aMano, setAMano] = useState("");
  const [interno, setInterno] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [modo, setModo] = useState(plazoInicial);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState(null); // {id, texto}: salió, con algo que decir
  const d = DESTINATARIOS[destinatario];
  const prestando = modo.modo === "prestar";

  const elegirPanol = (v) => {
    setPanol(v);
    // Lo del catálogo de otro pañol sale de la lista: la base no lo dejaría.
    setRenglones((xs) => xs.filter((r) => !r.herramienta_id || !r.panol || r.panol === v));
    try { window.localStorage.setItem(LS_PANOL, v); } catch { /* sin almacenamiento */ }
  };

  const opciones = useMemo(() => {
    const q = pelar(busca);
    return delPanolOSinAsignar(catalogo, panol)
      .filter((h) => !q || [h.nombre, h.codigo].some((v) => pelar(v).includes(q)))
      .slice(0, 30);
  }, [catalogo, panol, busca]);

  const sumarDelCatalogo = (h) => setRenglones((xs) => sumarRenglon(xs, {
    herramienta_id: h.id, nombre: h.nombre, codigo: h.codigo, panol: h.panol,
    cantidad: 1, tope: Math.max(Number(h.disponibles) || 0, 0),
  }));
  const sumarAMano = (nombre) => {
    const n = String(nombre || "").trim();
    if (!n) return;
    setRenglones((xs) => sumarRenglon(xs, { nombre: n, cantidad: 1 }));
    setAMano("");
  };

  const confirmar = async () => {
    const pl = plazoElegido(modo);
    if (!pl.ok) { setError(pl.error); return; }
    setTrabajando(true);
    setError("");
    /* Un préstamo lo dice en el papel: el que firma tiene que leer hasta
       cuándo la tiene. */
    const obs = [String(observaciones || "").trim(),
                 pl.definitivo ? "" : `Préstamo: devolver el ${diaAr(pl.vence)}`]
      .filter(Boolean).join(" · ");
    const r = await entregar({ persona, destinatario, panol, renglones,
                               interno, observaciones: obs,
                               definitivo: pl.definitivo, venceEl: pl.vence });
    setTrabajando(false);
    if (!r.ok) { setError(r.error); return; }
    if (r.aviso) { setAviso({ id: r.id, texto: r.aviso }); return; }
    onHecho(r.id);
  };

  if (aviso) {
    return (
      <section className="hrr-caja-nueva hrr-entrega" aria-label="Nueva entrega">
        <p className="hrr-aviso hrr-aviso-wa">{aviso.texto}</p>
        <button type="button" className="hrr-btn hrr-btn--principal"
                onClick={() => onHecho(aviso.id)}>
          Abrir el papel
        </button>
      </section>
    );
  }

  return (
    <section className="hrr-caja-nueva hrr-entrega" aria-label="Nueva entrega">
      <p className="hrr-caja-nueva-titulo">
        <b>Entregar {d.que} · sale el papel para firmar</b>
        <button type="button" className="hrr-btn hrr-btn--chico" onClick={onCerrar}>Cancelar</button>
      </p>

      <AvisoWhatsapp dni={persona?.dni} nombre={nombreDe(persona)} prestar={prestando} />

      <label className="hrr-campo">
        ¿De qué pañol sale?
        <select value={panol} onChange={(e) => elegirPanol(e.target.value)} aria-label="Pañol">
          {PANOLES.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
      </label>

      <input type="search" placeholder={`Buscar en el catálogo de ${nombrePanol(panol, { corto: true })}`}
             value={busca} onChange={(e) => setBusca(e.target.value)}
             aria-label="Buscar en el catálogo" />
      <ul className="hrr-caja-gente">
        {opciones.map((h) => (
          <li key={h.id}>
            <button type="button" disabled={!hayLibres(h)} onClick={() => sumarDelCatalogo(h)}>
              <b>{h.nombre}</b>
              <small>
                {h.codigo}
                {/* Lo que la destraba es subir «Cuántas hay», no cargar
                    Stock en el depósito (Nico, 20/10). Esta lista sólo la ve
                    quien tiene `cargar.taller`, que es el mismo permiso de
                    Administrar: el paso que se le dice lo puede hacer. */}
                {hayLibres(h)
                  ? ` · ${Number(h.disponibles) || 0} en el pañol`
                  : " · no queda ninguna — si llegaron más, subí «Cuántas hay» en Administrar"}
                {!h.panol ? " · sin pañol asignado" : ""}
              </small>
            </button>
          </li>
        ))}
        {opciones.length === 0 && (
          <li className="tenue">Nada del catálogo de este pañol coincide.</li>
        )}
      </ul>

      {/* Lo que todavía no está en el catálogo se escribe, como en el acta de
          papel. No descuenta stock (no hay de qué descontarlo), pero va al
          papel y a la caja. */}
      <div className="hrr-entrega-amano">
        <input type="text" value={aMano} placeholder="Otra cosa, escrita a mano"
               aria-label="Escribir otro elemento"
               onChange={(e) => setAMano(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sumarAMano(aMano); } }} />
        <button type="button" className="hrr-btn hrr-btn--chico"
                onClick={() => sumarAMano(aMano)} disabled={!aMano.trim()}>
          Sumar
        </button>
      </div>
      {destinatario === "chofer" && (
        <p className="hrr-entrega-chips" aria-label="Elementos comunes">
          {ELEMENTOS_COMUNES.map((e) => (
            <button key={e} type="button" className="hrr-chip" onClick={() => sumarAMano(e)}>
              + {e}
            </button>
          ))}
        </p>
      )}

      {renglones.length > 0 && (
        <ol className="hrr-entrega-lista" aria-label="Lo que se entrega">
          {renglones.map((r, i) => (
            <li key={`${r.herramienta_id || "m"}-${r.nombre}`}>
              <span>
                <b>{r.nombre}</b>
                {r.codigo && <small className="hrr-codigo"> {r.codigo}</small>}
                {!r.herramienta_id && <small className="tenue"> · a mano</small>}
              </span>
              <input type="number" min={1} max={r.herramienta_id ? r.tope : undefined}
                     value={r.cantidad} aria-label={`Cantidad de ${r.nombre}`}
                     onChange={(e) => setRenglones((xs) => cambiarCantidad(xs, i, e.target.value))} />
              <button type="button" className="hrr-btn hrr-btn--chico"
                      aria-label={`Quitar ${r.nombre}`}
                      onClick={() => setRenglones((xs) => xs.filter((_, j) => j !== i))}>✕</button>
              {/* Andrés (27/10): una observación por herramienta, por ella
                  misma o por cualquier otra razón. Va al papel y a la caja. */}
              <input type="text" className="hrr-entrega-nota" value={r.nota || ""}
                     maxLength={200} placeholder="Observación (opcional)"
                     aria-label={`Observación de ${r.nombre}`}
                     onChange={(e) => setRenglones((xs) => cambiarNota(xs, i, e.target.value))} />
            </li>
          ))}
        </ol>
      )}

      <div className="hrr-entrega-datos">
        <label className="hrr-campo">
          Interno {destinatario === "chofer" ? "(la unidad con la que sale)" : "(si va a una unidad)"}
          <input type="text" value={interno} onChange={(e) => setInterno(e.target.value)}
                 aria-label="Interno" />
        </label>
        <label className="hrr-campo">
          Observaciones
          <input type="text" value={observaciones}
                 onChange={(e) => setObservaciones(e.target.value)} aria-label="Observaciones" />
        </label>
      </div>

      <AsignarOPrestar valor={modo} onCambiar={setModo} plural={renglones.length > 1} />

      <button type="button" className="hrr-btn hrr-btn--principal"
              disabled={trabajando || renglones.length === 0}
              onClick={confirmar}>
        {trabajando ? "Guardando…"
          : `${prestando ? "Prestar" : "Entregar"} y armar el papel (${renglones.length})`}
      </button>
      {error && <p className="hrr-aviso">{error}</p>}
    </section>
  );
}

function Ficha({ f, puedeEditar, onSacar, trabajando }) {
  const [seguro, setSeguro] = useState(false);
  return (
    <div className="hrr-pos hrr-pos--caja">
      <span className="hrr-figura"><DibujoHerramienta tipo={f.tipo} /></span>
      <span className="hrr-chapa">
        <b>{f.nombre}</b>
        {f.cantidad > 1 && <span className="hrr-desde">× {f.cantidad}</span>}
        {f.desde && <span className="hrr-desde">desde {cuando(f.desde)}</span>}
        {(f.notas || []).map((n) => <em key={n} className="hrr-nota">{n}</em>)}
      </span>
      {puedeEditar && !seguro && (
        <button type="button" className="hrr-btn hrr-caja-sacar"
                onClick={() => setSeguro(true)} disabled={trabajando}
                aria-label={`Sacar ${f.nombre} de la caja`}>
          Sacar
        </button>
      )}
      {/* POR QUÉ SALE (07/10): volvió, se rompió o se perdió. Es lo que
          permite saber cuánto dura lo que se asigna. */}
      {puedeEditar && seguro && (
        <span className="hrr-caja-seguro" role="group" aria-label={`Por qué sale ${f.nombre}`}>
          ¿Por qué sale {f.cantidad > 1 ? "una" : ""}?{" "}
          {Object.entries(MOTIVOS_CIERRE).map(([k, rotulo]) => (
            <button key={k} type="button" className="hrr-btn hrr-btn--principal"
                    onClick={() => { setSeguro(false); onSacar(f, k); }}
                    disabled={trabajando}>{rotulo}</button>
          ))}{" "}
          <button type="button" className="hrr-btn"
                  onClick={() => setSeguro(false)}>Cancelar</button>
        </span>
      )}
    </div>
  );
}

/* Lo escrito a mano en sus papeles: no es del catálogo, así que no se saca
   de a uno — el papel que lo nombra sigue siendo el que vale. */
function FichaSuelta({ s }) {
  return (
    <div className="hrr-pos hrr-pos--caja hrr-pos--suelta">
      <span className="hrr-figura"><DibujoHerramienta tipo="generica" /></span>
      <span className="hrr-chapa">
        <b>{s.nombre}</b>
        {s.cantidad > 1 && <span className="hrr-desde">× {s.cantidad}</span>}
        <span className="hrr-desde">
          según el papel{s.desde ? ` del ${diaAr(s.desde)}` : ""}
        </span>
        {s.nota && <em className="hrr-nota">{s.nota}</em>}
      </span>
    </div>
  );
}

/* LOS PAPELES de la caja: cada entrega, si tiene la foto del papel firmado o
   si falta, con el papel para imprimir y dónde subir la foto (Nico: «En algún
   lugar de la caja me gustaría poner la foto del papel firmado»).

   Y QUITAR (Nico, 06/10: «le cargué más de una vez a la misma caja sin querer
   y quiero poder quitarla»): cada papel se puede quitar, con confirmación. Los
   que repiten a otro llevan la marca «Repetido», que es lo que se busca. */
function Papeles({ entregas, puedeEditar, onSubido, onQuitar, trabajando }) {
  const [seguro, setSeguro] = useState(null); // id del papel por quitar
  const repetidos = useMemo(() => papelesRepetidos(entregas), [entregas]);
  if (!entregas.length) return null;
  const faltan = entregas.filter((e) => estadoPapel(e) !== "firmado").length;
  const fecha = (id) => {
    const e = entregas.find((x) => x.id === id);
    return e?.entregado_en ? diaAr(e.entregado_en) : `#${id}`;
  };
  return (
    <section className="hrr-papeles" aria-label="Papeles firmados">
      <h4>
        Papeles ({entregas.length})
        {faltan > 0
          ? <span className="cst-estado cst--falta"> · a {faltan} le falta la foto firmada</span>
          : <span className="cst-estado cst--ok"> · todos firmados</span>}
      </h4>
      {puedeEditar && repetidos.size > 0 && (
        <p className="hrr-aviso hrr-aviso--suave">
          {repetidos.size === 1
            ? "Hay un papel repetido: dice lo mismo que otro. Quitá el que sobra."
            : `Hay ${repetidos.size} papeles repetidos: dicen lo mismo que otro. Quitá los que sobran.`}
        </p>
      )}
      <ul>
        {entregas.map((e) => {
          const est = ESTADOS_PAPEL[estadoPapel(e)];
          const items = Array.isArray(e.items) ? e.items : [];
          const original = repetidos.get(e.id);
          return (
            <li key={e.id} className={`hrr-papel${original ? " hrr-papel--repetido" : ""}`}>
              <span className="hrr-papel-que">
                <b>{e.entregado_en ? diaAr(e.entregado_en) : "Sin fecha"}</b>
                {" · "}{items.length} renglón{items.length === 1 ? "" : "es"}
                {e.panol ? ` · ${nombrePanol(e.panol, { corto: true })}` : ""}
                {e.origen === "relevamiento" ? " · lo que tenía" : ""}
                {original && (
                  <span className="hrr-repetido"> · Repetido (igual al del {fecha(original)})</span>
                )}
                <small className="tenue">
                  {items.slice(0, 4).map(renglonTexto).join(", ")}
                  {items.length > 4 ? "…" : ""}
                </small>
              </span>
              <span className={`cst-estado ${est.clase}`}>
                {est.icono} {est.rotulo}
                {e.foto_subida_en ? ` · ${cuando(e.foto_subida_en)}` : ""}
              </span>
              <span className="hrr-papel-acciones">
                <Link className="hrr-btn hrr-btn--chico"
                      to={`/taller/herramientas/constancia/${e.id}`}>
                  {e.foto_path ? "Ver el papel firmado" : "🖨️ Imprimir"}
                </Link>
                {puedeEditar && !e.foto_path && (
                  <SubirPapel entrega={e} onSubido={onSubido} chico />
                )}
                {puedeEditar && seguro !== e.id && (
                  <button type="button" className="hrr-btn hrr-btn--chico"
                          disabled={trabajando}
                          aria-label={`Quitar el papel del ${e.entregado_en ? diaAr(e.entregado_en) : "sin fecha"}`}
                          onClick={() => setSeguro(e.id)}>
                    Quitar
                  </button>
                )}
              </span>
              {puedeEditar && seguro === e.id && (
                <span className="hrr-caja-seguro hrr-papel-seguro" role="group"
                      aria-label="Confirmar quitar el papel">
                  {e.foto_path
                    ? "Este papel tiene la foto firmada. "
                    : ""}
                  ¿Quitarlo? Lo que nombra sale de la caja y lo del catálogo vuelve al
                  pañol. Queda anulado, no se borra.{" "}
                  <button type="button" className="hrr-btn hrr-btn--principal"
                          disabled={trabajando}
                          onClick={() => { setSeguro(null); onQuitar(e); }}>
                    Sí, quitar
                  </button>{" "}
                  <button type="button" className="hrr-btn" onClick={() => setSeguro(null)}>
                    No
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* QUITAR LA CAJA ENTERA: para la que se armó por error o la de alguien que ya
   no tiene nada. Se dice ANTES qué va a pasar (`planQuitarCaja`): los papeles
   se anulan, lo del catálogo vuelve al pañol, y lo prestado no se toca. Nada
   se borra. */
function QuitarCaja({ plan, nombre, trabajando, onQuitar }) {
  const [seguro, setSeguro] = useState(false);
  const vuelven = plan.anular.length + plan.devolver.length;
  if (!plan.papeles.length && !vuelven) return null;
  if (!seguro) {
    return (
      <p className="hrr-quitar-caja">
        <button type="button" className="hrr-btn hrr-btn--chico" disabled={trabajando}
                onClick={() => setSeguro(true)}>
          🗑️ Quitar esta caja
        </button>
      </p>
    );
  }
  return (
    <div className="hrr-aviso hrr-quitar-caja-seguro" role="group" aria-label="Confirmar quitar la caja">
      <p><b>¿Quitar la caja de {nombre}?</b></p>
      <ul>
        {plan.papeles.length > 0 && (
          <li>
            Se anula{plan.papeles.length === 1 ? "" : "n"} {plan.papeles.length} papel
            {plan.papeles.length === 1 ? "" : "es"}
            {plan.firmados > 0 ? ` (${plan.firmados} con la foto firmada)` : ""}.
          </li>
        )}
        {vuelven > 0 && (
          <li>
            {vuelven} herramienta{vuelven === 1 ? "" : "s"} del catálogo
            {vuelven === 1 ? " vuelve" : " vuelven"} al pañol.
          </li>
        )}
        {plan.prestadas > 0 && (
          <li>Lo que tiene prestado del pañol ({plan.prestadas}) no se toca.</li>
        )}
        <li>No se borra nada: queda en el historial quién la quitó y cuándo.</li>
      </ul>
      <button type="button" className="hrr-btn hrr-btn--principal" disabled={trabajando}
              onClick={() => { setSeguro(false); onQuitar(); }}>
        {trabajando ? "Quitando…" : "Sí, quitar la caja"}
      </button>{" "}
      <button type="button" className="hrr-btn" onClick={() => setSeguro(false)}>No</button>
    </div>
  );
}

const CajaHerramientas = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = puedeVerEntregas(permisos);
  const puedeEditar = permisos !== null && permisos.includes("cargar.taller");
  const { dni: dniCrudo = "" } = useParams();
  const dni = decodeURIComponent(dniCrudo);
  const ir = useNavigate();

  const [persona, setPersona] = useState(null);
  const [prestamos, setPrestamos] = useState(undefined); // undefined = cargando
  const [entregas, setEntregas] = useState([]);
  const [error, setError] = useState("");
  const [catalogo, setCatalogo] = useState([]);
  const [entregando, setEntregando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [errorAccion, setErrorAccion] = useState("");
  const [devoluciones, setDevoluciones] = useState([]);

  const cargar = useCallback(async () => {
    const [r, c, e, d] = await Promise.all([
      traerLoQueTiene(dni), traerTablero(), entregasDe(dni), devolucionesDe(dni)]);
    setPrestamos(r.filas || []);
    setError(r.error || e?.error || "");
    setCatalogo(c.filas || []);
    setEntregas(e?.filas || []);
    setDevoluciones(Array.isArray(d) ? d : []);
  }, [dni]);

  useEffect(() => { if (permitido) cargar(); }, [permitido, cargar]);

  useEffect(() => {
    if (!permitido) return undefined;
    let vivo = true;
    traerPersonas(dni).then((r) => {
      if (!vivo) return;
      setPersona((r.filas || []).find((p) => String(p.dni) === String(dni)) || null);
    });
    return () => { vivo = false; };
  }, [permitido, dni]);

  const { asignadas, prestadas } = useMemo(
    () => ordenLoQueTiene(prestamos || []), [prestamos]);
  const contenido = useMemo(() => contenidoDeCaja(asignadas), [asignadas]);
  const sueltos = useMemo(() => sueltosDe(entregas), [entregas]);

  if (!permitido) {
    return <PuertaCerrada />;
  }

  /* El nombre sale del padrón; si la persona ya no está activa, del nombre
     guardado en la entrega, que es como se la conocía cuando se le dio. */
  const nombre = persona ? nombreDe(persona)
    : ((prestamos || [])[0]?.persona_nombre || entregas[0]?.persona_nombre || `DNI ${dni}`);
  const quien = persona || { dni, apellido: nombre };
  const destinatario = persona ? destinatarioDe(persona)
    : (entregas[0]?.destinatario || "mecanico");
  const d = DESTINATARIOS[destinatario];
  const cuantas = contenido.length + sueltos.length;

  const alPapel = (id) => ir(`/taller/herramientas/constancia/${id}?imprimir=1`);

  const sacar = async (f, motivo = "devuelta") => {
    setTrabajando(true);
    setErrorAccion("");
    const r = await devolver({ prestamoId: f.prestamos[0], nota: "sale de la caja", motivo });
    setTrabajando(false);
    if (!r.ok) setErrorAccion(r.error || "No pude sacarla.");
    cargar();
  };

  const quitarUnPapel = async (e) => {
    setTrabajando(true);
    setErrorAccion("");
    const r = await quitarPapel(e.id);
    setTrabajando(false);
    if (!r.ok) setErrorAccion(r.error || "No pude quitar el papel.");
    else if (r.aviso) setErrorAccion(r.aviso);
    cargar();
  };

  const plan = planQuitarCaja(prestamos || [], entregas);
  const quitarLaCaja = async () => {
    setTrabajando(true);
    setErrorAccion("");
    const r = await quitarCaja(plan);
    setTrabajando(false);
    if (!r.ok) { setErrorAccion(r.error); cargar(); return; }
    ir("/taller/herramientas");
  };

  const papelDeHoy = async () => {
    setTrabajando(true);
    setErrorAccion("");
    const r = await papelDeLoQueTiene({ persona: quien, destinatario,
                                        items: itemsDeLoQueTiene(contenido, sueltos) });
    setTrabajando(false);
    if (!r.ok) { setErrorAccion(r.error); return; }
    alPapel(r.id);
  };

  return (
    <div className="hrr">
      <p className="hrr-caja-volver">
        <Link to="/taller/herramientas">← Todas las cajas</Link>
      </p>
      <div className="hrr-cabecera">
        <h1 className="hrr-titulo">🧰 {nombre}</h1>
        <div className="hrr-cuenta">
          <span className={`hrr-destinatario hrr-destinatario--${destinatario}`}>
            {d.icono} {d.rotulo}
          </span>
          <span className="hrr-ind">
            <b>{cuantas}</b> <small>{d.que}</small>
          </span>
        </div>
      </div>
      {persona && (
        <p className="tenue hrr-caja-dato">
          DNI {persona.dni}{persona.legajo ? ` · legajo ${persona.legajo}` : ""}
          {persona.sector ? ` · ${persona.sector}` : ""}
          {persona.puesto ? ` · ${persona.puesto}` : ""}
        </p>
      )}

      {puedeEditar && !entregando && (
        <div className="hrr-barra-edicion">
          <button type="button" className="hrr-btn hrr-btn--principal hrr-btn--chico"
                  onClick={() => { setEntregando(true); setErrorAccion(""); }}>
            ➕ Entregar {d.que}
          </button>
          {cuantas > 0 && (
            <button type="button" className="hrr-btn hrr-btn--chico"
                    onClick={papelDeHoy} disabled={trabajando}
                    title="Un papel con todo lo que tiene hoy, para que lo firme">
              📄 Papel de lo que tiene hoy
            </button>
          )}
        </div>
      )}
      {entregando && (
        <NuevaEntrega persona={quien} destinatario={destinatario} catalogo={catalogo}
                      onHecho={alPapel}
                      onCerrar={() => setEntregando(false)} />
      )}
      {errorAccion && <p className="hrr-aviso">{errorAccion}</p>}

      {error && (
        <p className="hrr-aviso">No se pudo consultar.</p>
      )}
      {prestamos === undefined && <p className="hrr-vacio">Abriendo la caja…</p>}

      {prestamos && !error && cuantas === 0 && (
        <div className="hrr-vacio">
          <h3>La caja está vacía</h3>
          <p>
            {puedeEditar
              ? `Entregale ${d.que} con «Entregar ${d.que}»: sale el papel para que lo firme.`
              : `Esta persona no tiene ${d.que} asignados.`}
          </p>
        </div>
      )}

      {cuantas > 0 && (
        <div className="hrr-tablero hrr-tablero--caja">
          {contenido.map((f) => (
            <Ficha key={f.herramienta_id} f={f} puedeEditar={puedeEditar}
                   onSacar={sacar} trabajando={trabajando} />
          ))}
          {sueltos.map((s, i) => <FichaSuelta key={`${s.entrega_id}-${i}`} s={s} />)}
        </div>
      )}

      <Papeles entregas={entregas} puedeEditar={puedeEditar} onSubido={cargar}
               onQuitar={quitarUnPapel} trabajando={trabajando} />

      {prestadas.length > 0 && (
        <section className="hrr-tiene" aria-label="Prestado del pañol">
          <h4>Además tiene prestado del pañol ({prestadas.length})</h4>
          <ul>
            {prestadas.map((p) => (
              <li key={p.id}>
                <Link className="hrr-tiene-item" to="/taller/herramientas/panol">
                  <b>{p.herramienta?.nombre || p.herramienta?.codigo || "Herramienta"}</b>
                  <small>
                    prestada{p.retirado_en ? ` · desde ${cuando(p.retirado_en)}` : ""}
                    {p.vence_el ? ` · ${plazoDePrestamo(p)}` : ""}
                    {notaDeEntrega(p.nota) ? ` · ${notaDeEntrega(p.nota)}` : ""}
                  </small>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* La última devolución se ve con la caja vacía: es la respuesta a
          «¿y esto?» de quien abre la caja de alguien que ya se fue. Las
          anteriores, si las hay, quedan plegadas abajo (ticket 477). */}
      {prestamos && !error && cuantas === 0 && (
        <UltimaDevolucion devolucion={devoluciones[0]} anteriores={devoluciones.slice(1)} />
      )}

      {puedeEditar && prestamos && !error && (
        <DevolvioTodo dni={dni} nombre={nombre} prestamos={prestamos} entregas={entregas}
                      onHecho={cargar} />
      )}

      {puedeEditar && prestamos && !error && (
        <QuitarCaja plan={plan} nombre={nombre} trabajando={trabajando}
                    onQuitar={quitarLaCaja} />
      )}
    </div>
  );
};

export default CajaHerramientas;
